import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';

export const FUNNEL_STEPS = {
  REDIRECT: 'redirect',
  STORE_LIST: 'store_list',
  HOME: 'home',
  PHONE_NUMBER_SCREEN: 'number_screen',
  ACCOUNT: 'account',
  SCAN: 'scan',
  FID_SCAN: 'fid_scan',
  INVITATION: 'invitation',
  PROMO: 'promo',
  FID_COUPON: 'fid_coupon',
  CART: 'cart',
  SCAN_CHECK: 'scancheck',
  RECEIPT: 'receipt',
};

const getFunnelStepOrder = (stepName) => {
  const stepOrder = {
    [FUNNEL_STEPS.REDIRECT]: 1,
    [FUNNEL_STEPS.STORE_LIST]: 2,
    [FUNNEL_STEPS.HOME]: 3,
    [FUNNEL_STEPS.PHONE_NUMBER_SCREEN]: 4,
    [FUNNEL_STEPS.ACCOUNT]: 5,
    [FUNNEL_STEPS.SCAN]: 6,
    [FUNNEL_STEPS.FID_SCAN]: 7,
    [FUNNEL_STEPS.INVITATION]: 8,
    [FUNNEL_STEPS.PROMO]: 9,
    [FUNNEL_STEPS.FID_COUPON]: 10,
    [FUNNEL_STEPS.CART]: 11,
    [FUNNEL_STEPS.SCAN_CHECK]: 12,
    [FUNNEL_STEPS.RECEIPT]: 13,
  };
  return stepOrder[stepName] || 0;
};

const getFunnelProgress = (stepName) => {
  const totalSteps = Object.keys(FUNNEL_STEPS).length;
  const currentOrder = getFunnelStepOrder(stepName);
  return Math.round((currentOrder / totalSteps) * 100);
};

export const useFunnelTracking = () => {
  const posthog = usePostHog();
  const [stepStartTime, setStepStartTime] = useState(null);

  const trackStep = (stepName, metadata = {}) => {
    const now = Date.now();
    const timeSpent = stepStartTime ? now - stepStartTime : 0;

    posthog?.capture(`step_${stepName}`, {
      step_name: stepName,
      step_order: getFunnelStepOrder(stepName),
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      time_spent_ms: timeSpent,
      time_spent_seconds: Math.round(timeSpent / 1000),
      ...metadata,
    });
  };

  const trackPageView = (stepName, metadata = {}) => {
    trackStep(stepName, metadata);

    posthog?.capture('$pageview', {
      $current_url: window.location.href,
      page_title: document.title,
      funnel_step: stepName,
      step_order: getFunnelStepOrder(stepName),
      funnel_progress_percentage: getFunnelProgress(stepName),
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  };

  const trackAbandonment = (currentStep, reason = 'unknown') => {
    posthog?.capture('funnel_abandoned', {
      abandonment_step: currentStep,
      step_order: getFunnelStepOrder(currentStep),
      abandonment_reason: reason,
      funnel_progress_percentage: getFunnelProgress(currentStep),
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
    });
  };

  return {
    trackPageViewRedirect: (data = {}) => trackPageView(FUNNEL_STEPS.REDIRECT, data),
    trackPageViewStoreList: (data = {}) => trackPageView(FUNNEL_STEPS.STORE_LIST, data),
    trackPageViewHome: (data = {}) => trackPageView(FUNNEL_STEPS.HOME, data),
    trackPageViewPhoneNumber: (data = {}) => trackPageView(FUNNEL_STEPS.PHONE_NUMBER_SCREEN, data),
    trackPageViewAccount: (data = {}) => trackPageView(FUNNEL_STEPS.ACCOUNT, data),
    trackPageViewScan: (data = {}) => trackPageView(FUNNEL_STEPS.SCAN, data),
    trackPageViewFidScan: (data = {}) => trackPageView(FUNNEL_STEPS.FID_SCAN, data),
    trackPageViewInvitation: (data = {}) => trackPageView(FUNNEL_STEPS.INVITATION, data),
    trackPageViewPromo: (data = {}) => trackPageView(FUNNEL_STEPS.PROMO, data),
    trackPageViewCoupon: (data = {}) => trackPageView(FUNNEL_STEPS.FID_COUPON, data),
    trackPageViewCart: (data = {}) => trackPageView(FUNNEL_STEPS.CART, data),
    trackPageViewScanCheck: (data = {}) => trackPageView(FUNNEL_STEPS.SCAN_CHECK, data),
    trackPageViewReceipt: (data = {}) => trackPageView(FUNNEL_STEPS.RECEIPT, data),

    trackAbandonment,

    trackFunnelOrderPaid: (data = {}) => {
      posthog?.capture('funnel_order_paid', {
        completion_time: new Date().toISOString(),
        ...data,
      });
    },

    trackFunnelCompleted: (data = {}) => {
      const totalSteps = Object.keys(FUNNEL_STEPS).length;
      posthog?.capture('funnel_completed', {
        completion_time: new Date().toISOString(),
        funnel_success: true,
        total_steps: totalSteps,
        ...data,
      });
    },

    posthog,
  };
};

